import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { MODULE_PAGE_CSS } from './moduleBannerStyles';

const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
const today = () => new Date().toISOString().split('T')[0];
const CERT_TYPES = ['Gas Safety Certificate', 'EPC (Energy Performance Certificate)', 'EICR (Electrical Installation)', 'HMO Licence', 'Buildings Insurance', 'Contents Insurance', 'PAT Testing', 'Fire Risk Assessment', 'Legionella Risk Assessment', 'Other'];
const CERT_FILTERS = ['All', 'Valid', 'Expiring', 'Expiring ≤30d', 'Expired', 'No document', 'History'] as const;

const isStatutory = (certType: string) => {
  const t = (certType || '').toLowerCase();
  return t.includes('gas') || t.includes('epc') || t.includes('energy') || t.includes('eicr') || t.includes('electrical');
};

const certDays = (c: any): number | null => {
  if (!c.expiry_date) return null;
  return Math.floor((new Date(c.expiry_date).getTime() - Date.now()) / 86400000);
};
const certStatus = (c: any) => {
  const days = certDays(c);
  if (days === null) return { label: 'No Expiry', bg: '#f1f5f9', color: '#64748b', icon: '📄', key: 'valid' as const };
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, bg: '#fee2e2', color: '#dc2626', icon: '🚨', key: 'expired' as const };
  if (days <= 30) return { label: `${days}d left`, bg: '#fee2e2', color: '#dc2626', icon: '⚠️', key: 'expiring' as const };
  if (days <= 60) return { label: `${days}d left`, bg: '#fef3c7', color: '#d97706', icon: '🟡', key: 'expiring' as const };
  return { label: `${days}d left`, bg: '#f0fdf4', color: '#16a34a', icon: '✅', key: 'valid' as const };
};
const sortScore = (c: any) => { const d = certDays(c); return d === null ? 9999 : d; };

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');
.cv-wrap * { box-sizing: border-box; }
.cv-wrap { font-family: 'Bricolage Grotesque', system-ui, sans-serif; }
.alert-banner { border-radius: 14px; padding: 14px 18px; margin-bottom: 14px; border: 1.5px solid; display: flex; align-items: flex-start; gap: 12px; }
.alert-body { flex: 1; min-width: 0; }
.alert-title { font-size: 13px; font-weight: 800; margin-bottom: 8px; }
.alert-item { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 7px 0; border-top: 1px solid rgba(0,0,0,.06); flex-wrap: wrap; }
.alert-name { font-size: 13px; font-weight: 700; color: #0f172a; }
.alert-meta { font-size: 12px; color: #64748b; margin-top: 2px; }
.toolbar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 20px; }
.search-box { display: flex; align-items: center; gap: 8px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 8px 14px; flex: 1; min-width: 180px; max-width: 260px; }
.search-box input { border: none; outline: none; font-size: 13px; font-family: 'Bricolage Grotesque', sans-serif; color: #0f172a; background: transparent; width: 100%; }
.cv-filter { padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff; font-size: 13px; font-weight: 700; color: #012169; appearance: none; min-width: 180px; cursor: pointer; font-family: inherit; }
.cv-alert-slim { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 12px 16px; border-radius: 12px; margin-bottom: 14px; font-size: 13px; font-weight: 700; }
.cv-alert-slim.red { background: #fef2f2; border: 1.5px solid #fecaca; color: #991b1b; }
.cv-alert-slim.amber { background: #fffbeb; border: 1.5px solid #fde68a; color: #92400e; }
.share-badge { font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 6px; margin-left: 6px; vertical-align: middle; }
.share-badge.on { background: #dbeafe; color: #1d4ed8; }
.share-badge.off { background: #fef3c7; color: #b45309; }
.history-badge { font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 6px; background: #f1f5f9; color: #64748b; margin-left: 6px; }
.result-info { font-size: 12px; color: #94a3b8; font-weight: 600; margin-left: auto; }
.cv-table-wrap { border-radius: 14px; border: 1.5px solid #e9ecef; overflow: hidden; }
.cv-table { width: 100%; border-collapse: collapse; background: #fff; }
.cv-table th { padding: 11px 16px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #94a3b8; background: #f8fafc; border-bottom: 1.5px solid #e9ecef; white-space: nowrap; }
.cv-table td { padding: 14px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.cv-table tbody tr.cv-data-row:hover td { background: #fafbfc; }
.cv-table tbody tr.cv-edit-row td { background: #f0fdfa; border-bottom: 1.5px solid #0d9488; padding: 20px 18px; }
.cert-type { font-size: 14px; font-weight: 700; color: #0f172a; }
.cert-note { font-size: 11px; color: #94a3b8; margin-top: 2px; font-style: italic; }
.cert-prop { font-size: 13px; color: #374151; font-weight: 600; }
.cert-date { font-size: 13px; color: #374151; font-family: 'DM Mono', monospace; }
.status-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; border-radius: 8px; font-size: 13px; font-weight: 800; white-space: nowrap; }
.btn-act { padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; border: 1.5px solid; transition: all .15s; font-family: 'Bricolage Grotesque', sans-serif; white-space: nowrap; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; }
.btn-act:hover { transform: translateY(-1px); filter: brightness(1.05); }
.actions-cell { display: flex; gap: 6px; align-items: center; justify-content: flex-end; flex-wrap: wrap; }
.toast-ok { background: #f0fdf4; color: #16a34a; border: 1.5px solid #bbf7d0; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
.toast-err { background: #fff1f1; color: #dc2626; border: 1.5px solid #fecaca; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
.cv-panel { background: #fff; border: 1.5px solid #0d9488; border-radius: 16px; padding: 24px; margin-bottom: 24px; }
.cv-panel-title { font-size: 16px; font-weight: 800; color: #0d9488; margin: 0 0 18px; }
.cv-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
.cv-grid .span2 { grid-column: span 2; }
.field-label { display: block; font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 5px; }
.field-input { width: 100%; padding: 9px 12px; border: 1.5px solid #d1d5db; border-radius: 9px; font-size: 14px; font-family: 'Bricolage Grotesque', sans-serif; color: #0f172a; background: #fff; outline: none; }
.field-input:focus { border-color: #0d9488; }
.file-label { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; background: #f0fdfa; border: 1.5px dashed #0d9488; border-radius: 9px; padding: 9px 16px; font-size: 13px; color: #0d9488; font-weight: 700; }
.panel-actions { display: flex; gap: 10px; margin-top: 18px; }
.btn-primary { background: #0d9488; color: #fff; border: none; border-radius: 9px; padding: 10px 24px; font-weight: 800; font-size: 14px; cursor: pointer; font-family: 'Bricolage Grotesque', sans-serif; }
.btn-secondary { background: #fff; color: #374151; border: 1.5px solid #d1d5db; border-radius: 9px; padding: 10px 20px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: 'Bricolage Grotesque', sans-serif; }
.cv-empty { text-align: center; padding: 64px 32px; background: #fff; border: 2px dashed #e2e8f0; border-radius: 18px; }
.cv-empty .empty-icon { font-size: 48px; margin-bottom: 14px; }
.share-row { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #374151; margin-top: 4px; }
@media (max-width: 700px) { .cv-grid .span2 { grid-column: span 1; } }
`;

const BLANK_FORM = { property_address: '', cert_type: 'Gas Safety Certificate', issue_date: '', expiry_date: '', notes: '', shared_with_tenant: true };

export default function Certificates() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [certs, setCerts] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterTab, setFilterTab] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [renewFromId, setRenewFromId] = useState<number | null>(null);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certFileName, setCertFileName] = useState('');
  const [editingCertId, setEditingCertId] = useState<number | null>(null);
  const [editCertForm, setEditCertForm] = useState<any>({});
  const [deletingCertId, setDeletingCertId] = useState<number | null>(null);
  const [replaceCertFile, setReplaceCertFile] = useState<File | null>(null);
  const [complianceMatrix, setComplianceMatrix] = useState<any[]>([]);
  const [matrixSummary, setMatrixSummary] = useState<any>(null);
  const [showCompliance, setShowCompliance] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [showUrgent30, setShowUrgent30] = useState(false);
  const [showExpiring60, setShowExpiring60] = useState(false);

  const load = useCallback(async () => {
    if (!token) return; setLoading(true);
    const [c, p, m] = await Promise.all([
      fetch(`${API_URL}/landlord/certificates`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/landlord/properties`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/landlord/compliance-matrix`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({ matrix: [], summary: null })),
    ]);
    setCerts(c.certificates || []);
    setProperties(p.properties || []);
    setComplianceMatrix(m.matrix || []);
    setMatrixSummary(m.summary || null);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openAdd = (prefill?: { property_address: string; cert_type: string; supersede_id?: number }) => {
    const certType = prefill?.cert_type || 'Gas Safety Certificate';
    setRenewFromId(prefill?.supersede_id || null);
    setForm({
      property_address: prefill?.property_address || '',
      cert_type: certType,
      issue_date: prefill?.supersede_id ? today() : '',
      expiry_date: '',
      notes: '',
      shared_with_tenant: isStatutory(certType),
    });
    setCertFile(null); setCertFileName(''); setEditingCertId(null); setError('');
    setShowAdd(true);
  };

  const save = async () => {
    if (!form.property_address || !form.cert_type) { setError('Property and certificate type are required'); return; }
    if (!form.issue_date) { setError('Issue date is required'); return; }
    setSaving(true); setError('');
    const body: any = { ...form };
    if (renewFromId) body.supersede_id = renewFromId;
    const r = await fetch(`${API_URL}/landlord/certificates`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || 'Failed to save'); setSaving(false); return; }
    if (certFile && d.certificate?.id) {
      const fd = new FormData(); fd.append('file', certFile);
      await fetch(`${API_URL}/landlord/certificates/${d.certificate.id}/file`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    }
    setSuccess(renewFromId ? 'Certificate renewed — previous record archived' : 'Certificate saved');
    setShowAdd(false); setRenewFromId(null);
    load(); setSaving(false); setTimeout(() => setSuccess(''), 3000);
  };

  const toggleShare = async (id: number, current: boolean) => {
    await fetch(`${API_URL}/landlord/certificates/${id}/share`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ shared_with_tenant: !current }),
    });
    load();
  };

  const logRenewalJob = (c: any) => {
    navigate('/landlord/maintenance', {
      state: {
        prefill: {
          property_address: c.property_address,
          description: `Renew ${c.cert_type}${c.expiry_date ? ` — expired ${fmt(c.expiry_date)}` : ''}`,
          priority: 'urgent',
          due_date: today(),
          notes: 'Logged from Docs & Certs — buildings insurance may be at risk until renewed',
        },
      },
    });
  };

  const deleteCert = async (id: number) => {
    if (!window.confirm('Delete this certificate record? This cannot be undone.')) return;
    setDeletingCertId(id);
    await fetch(`${API_URL}/landlord/certificates/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load(); setDeletingCertId(null);
  };

  const startEditCert = (c: any) => {
    setEditingCertId(c.id); setReplaceCertFile(null); setShowAdd(false);
    setEditCertForm({
      cert_type: c.cert_type,
      issue_date: c.issue_date?.split('T')[0] || '',
      expiry_date: c.expiry_date?.split('T')[0] || '',
      notes: c.description || '',
      shared_with_tenant: Boolean(c.shared_with_tenant),
    });
  };

  const saveEditCert = async (id: number) => {
    setSaving(true); setError('');
    const r = await fetch(`${API_URL}/landlord/certificates/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(editCertForm),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || 'Failed to update'); setSaving(false); return; }
    if (replaceCertFile) {
      const fd = new FormData(); fd.append('file', replaceCertFile);
      await fetch(`${API_URL}/landlord/certificates/${id}/file`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    }
    setSuccess('Certificate updated'); setEditingCertId(null); load(); setSaving(false); setTimeout(() => setSuccess(''), 3000);
  };

  const currentCerts = certs.filter(c => !c.superseded_by);
  const historyCerts = certs.filter(c => c.superseded_by);
  const withDocs = currentCerts.filter(c => c.file_url).length;

  const counts = {
    valid: currentCerts.filter(c => certStatus(c).key === 'valid').length,
    expiring: currentCerts.filter(c => certStatus(c).key === 'expiring').length,
    expired: currentCerts.filter(c => certStatus(c).key === 'expired').length,
    no_doc: currentCerts.filter(c => !c.file_url).length,
    history: historyCerts.length,
  };

  const sorted = [...(filterTab === 'History' ? historyCerts : currentCerts)].sort((a, b) => sortScore(a) - sortScore(b));

  const displayed = sorted.filter(c => {
    const st = certStatus(c);
    const days = certDays(c);
    if (filterTab === 'Valid' && st.key !== 'valid') return false;
    if (filterTab === 'Expiring' && st.key !== 'expiring') return false;
    if (filterTab === 'Expiring ≤30d' && !(days !== null && days >= 0 && days <= 30)) return false;
    if (filterTab === 'Expired' && st.key !== 'expired') return false;
    if (filterTab === 'No document' && c.file_url) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    return c.cert_type.toLowerCase().includes(q) || c.property_address.toLowerCase().includes(q);
  });

  const filterCount = (f: string) => {
    if (f === 'Valid') return counts.valid;
    if (f === 'Expiring') return counts.expiring;
    if (f === 'Expiring ≤30d') return currentCerts.filter(c => { const d = certDays(c); return d !== null && d >= 0 && d <= 30; }).length;
    if (f === 'Expired') return counts.expired;
    if (f === 'No document') return counts.no_doc;
    if (f === 'History') return counts.history;
    return 0;
  };

  const expiredList = currentCerts.filter(c => certStatus(c).key === 'expired');
  const urgent30 = currentCerts.filter(c => { const d = certDays(c); return d !== null && d >= 0 && d <= 30; });
  const expiring60 = currentCerts.filter(c => { const d = certDays(c); return d !== null && d > 30 && d <= 60; });
  const insuranceRisk = expiredList.filter(c => isStatutory(c.cert_type));

  return (
    <div className="mod-page cv-wrap">
      <style>{MODULE_PAGE_CSS}{CSS}</style>

      <div className="mod-banner-wrap mod-banner-certs">
        <div className="mod-banner-glass">
          <div>
            <h1>📁 Docs &amp; Certs</h1>
            <p>
              {matrixSummary ? `${matrixSummary.compliant} of ${matrixSummary.total} properties compliant` : `${counts.valid} valid`}
              {' · '}{counts.expiring} expiring · {counts.expired} expired · {withDocs} with documents
            </p>
          </div>
          <div className="mod-banner-actions">
            <button type="button" className="mod-btn-glass" style={showCompliance ? { background: 'rgba(0,0,0,0.25)' } : undefined} onClick={() => setShowCompliance(v => !v)}>Compliance matrix</button>
            <button type="button" className="mod-btn-white" onClick={() => openAdd()}>+ Add certificate</button>
          </div>
        </div>
      </div>

      {showCompliance && complianceMatrix.length > 0 && (
        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>🏛 UK Compliance Matrix — required per property type</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {complianceMatrix.map((row: any) => (
              <div key={row.address} style={{ padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${row.compliant ? '#bbf7d0' : '#fecaca'}`, background: row.compliant ? '#f0fdf4' : '#fff1f1' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontWeight: 700 }}>{row.address} <span style={{ fontWeight: 500, color: '#64748b', fontSize: 12 }}>({row.property_type || 'Property'})</span></span>
                  <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 20, background: row.compliant ? '#dcfce7' : '#fee2e2', color: row.compliant ? '#16a34a' : '#dc2626' }}>
                    {row.compliant ? '✅ Compliant' : `⚠️ ${row.missing + row.expired} issue${row.missing + row.expired !== 1 ? 's' : ''}`}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {row.checks.map((chk: any, i: number) => (
                    <span key={i} title={chk.shared ? 'Shared with tenant' : chk.has_file ? 'Not shared with tenant' : ''} style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: chk.status === 'valid' ? '#f0fdf4' : chk.status === 'missing' ? '#f1f5f9' : '#fee2e2', color: chk.status === 'valid' ? '#16a34a' : chk.status === 'missing' ? '#64748b' : '#dc2626' }}>
                      {chk.status === 'valid' ? '✅' : chk.status === 'missing' ? '❓' : '🚨'} {chk.type.split('(')[0].trim().split(' ')[0]}
                      {chk.shared ? ' ↗' : chk.has_file && chk.status === 'valid' ? ' ⚠' : ''}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mod-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="mod-kpi mod-kpi-green">
          <div className="kl">Valid</div>
          <div className="kv">{counts.valid}</div>
          <div className="ks">compliant</div>
        </div>
        <div className="mod-kpi mod-kpi-amber">
          <div className="kl">Expiring ≤60d</div>
          <div className="kv">{counts.expiring}</div>
          <div className="ks">{counts.expiring > 0 ? 'plan renewal' : 'none'}</div>
        </div>
        <div className="mod-kpi mod-kpi-red">
          <div className="kl">Expired</div>
          <div className="kv">{counts.expired}</div>
          <div className="ks">{counts.expired > 0 ? 'renew now' : 'none'}</div>
        </div>
        <div className="mod-kpi mod-kpi-blue">
          <div className="kl">With documents</div>
          <div className="kv">{withDocs}</div>
          <div className="ks">uploaded</div>
        </div>
      </div>

      {expiredList.length > 0 && !showExpired && (
        <div className="cv-alert-slim red">
          <span>🚨 {expiredList.length} expired — {insuranceRisk.length > 0 ? 'insurance may be at risk' : 'renew immediately'}</span>
          <button type="button" className="btn-act" style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
            onClick={() => { setShowExpired(true); setFilterTab('Expired'); }}>View</button>
        </div>
      )}

      {urgent30.length > 0 && !showUrgent30 && (
        <div className="cv-alert-slim red">
          <span>⚠️ {urgent30.length} expiring within 30 days</span>
          <button type="button" className="btn-act" style={{ background: '#012169', color: '#fff', borderColor: '#012169' }}
            onClick={() => { setShowUrgent30(true); setFilterTab('Expiring ≤30d'); }}>View</button>
        </div>
      )}

      {expiring60.length > 0 && !showExpiring60 && (
        <div className="cv-alert-slim amber">
          <span>🟡 {expiring60.length} expiring within 60 days</span>
          <button type="button" className="btn-act" style={{ background: '#d97706', color: '#fff', borderColor: '#d97706' }}
            onClick={() => setShowExpiring60(true)}>View</button>
        </div>
      )}

      {showExpired && expiredList.length > 0 && (
        <div className="alert-banner" style={{ background: '#fff1f1', borderColor: '#fecaca', marginBottom: 14 }}>
          <div className="alert-body" style={{ width: '100%' }}>
            <div className="alert-title" style={{ color: '#dc2626', display: 'flex', justifyContent: 'space-between' }}>
              <span>Expired certificates{insuranceRisk.length > 0 ? ' — statutory certs may void insurance' : ''}</span>
              <button type="button" className="btn-act" style={{ background: '#fff', color: '#64748b', borderColor: '#e2e8f0' }} onClick={() => setShowExpired(false)}>Hide</button>
            </div>
            {expiredList.map(c => (
              <div key={c.id} className="alert-item">
                <div>
                  <div className="alert-name">{c.cert_type}</div>
                  <div className="alert-meta">{c.property_address} · expired {fmt(c.expiry_date)}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn-act" style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
                    onClick={() => openAdd({ property_address: c.property_address, cert_type: c.cert_type, supersede_id: c.id })}>🔄 Renew</button>
                  <button className="btn-act" style={{ background: '#fff', color: '#012169', borderColor: '#93c5fd' }}
                    onClick={() => logRenewalJob(c)}>🛠️ Log job</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showUrgent30 && urgent30.length > 0 && (
        <div className="alert-banner" style={{ background: '#fff7ed', borderColor: '#fed7aa', marginBottom: 14 }}>
          <div className="alert-body" style={{ width: '100%' }}>
            <div className="alert-title" style={{ color: '#ea580c', display: 'flex', justifyContent: 'space-between' }}>
              <span>Expiring within 30 days</span>
              <button type="button" className="btn-act" style={{ background: '#fff', color: '#64748b', borderColor: '#e2e8f0' }} onClick={() => setShowUrgent30(false)}>Hide</button>
            </div>
            {urgent30.map(c => (
              <div key={c.id} className="alert-item">
                <div>
                  <div className="alert-name">{c.cert_type}</div>
                  <div className="alert-meta">{c.property_address} · expires {fmt(c.expiry_date)} ({certDays(c)}d)</div>
                </div>
                <button className="btn-act" style={{ background: '#ea580c', color: '#fff', borderColor: '#ea580c' }}
                  onClick={() => openAdd({ property_address: c.property_address, cert_type: c.cert_type, supersede_id: c.id })}>🔄 Renew</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showExpiring60 && expiring60.length > 0 && (
        <div className="alert-banner" style={{ background: '#fffbeb', borderColor: '#fde68a', marginBottom: 14 }}>
          <div className="alert-body" style={{ width: '100%' }}>
            <div className="alert-title" style={{ color: '#d97706', display: 'flex', justifyContent: 'space-between' }}>
              <span>Expiring within 60 days</span>
              <button type="button" className="btn-act" style={{ background: '#fff', color: '#64748b', borderColor: '#e2e8f0' }} onClick={() => setShowExpiring60(false)}>Hide</button>
            </div>
            {expiring60.map(c => (
              <div key={c.id} className="alert-item">
                <div>
                  <div className="alert-name">{c.cert_type}</div>
                  <div className="alert-meta">{c.property_address} · expires {fmt(c.expiry_date)} ({certDays(c)}d)</div>
                </div>
                <button className="btn-act" style={{ background: '#fff', color: '#d97706', borderColor: '#fde68a' }}
                  onClick={() => openAdd({ property_address: c.property_address, cert_type: c.cert_type, supersede_id: c.id })}>🔔 Plan</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="toast-err">❌ {error}</div>}
      {success && <div className="toast-ok">✅ {success}</div>}

      {showAdd && (
        <div className="cv-panel">
          <div className="cv-panel-title">
            {renewFromId ? `🔄 Renew — ${form.cert_type} · ${form.property_address}` : '+ Add Certificate Record'}
          </div>
          <div className="cv-grid">
            <div>
              <label className="field-label">Property *</label>
              <select value={form.property_address} onChange={e => setForm(p => ({ ...p, property_address: e.target.value }))} className="field-input" disabled={!!renewFromId}>
                <option value="">— Select property —</option>
                {properties.map((p: any) => <option key={p.id} value={p.address}>{p.address}, {p.postcode}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Certificate Type *</label>
              <select value={form.cert_type} onChange={e => setForm(p => ({ ...p, cert_type: e.target.value, shared_with_tenant: isStatutory(e.target.value) }))} className="field-input" disabled={!!renewFromId}>
                {CERT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Issue Date *</label>
              <input type="date" value={form.issue_date} onChange={e => setForm(p => ({ ...p, issue_date: e.target.value }))} className="field-input" />
            </div>
            <div>
              <label className="field-label">Valid Until</label>
              <input type="date" value={form.expiry_date} onChange={e => setForm(p => ({ ...p, expiry_date: e.target.value }))} className="field-input" />
            </div>
            <div className="span2">
              <label className="field-label">Notes (optional)</label>
              <input type="text" placeholder="e.g. Ref number, engineer name…" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="field-input" />
            </div>
            <div className="span2">
              <label className="share-row">
                <input type="checkbox" checked={form.shared_with_tenant} onChange={e => setForm(p => ({ ...p, shared_with_tenant: e.target.checked }))} />
                Share with tenant in portal (when document attached)
              </label>
            </div>
            <div className="span2">
              <label className="field-label">Attach document — PDF or JPG, max 10MB</label>
              <label className="file-label">
                📎 {certFileName || 'Choose file to upload…'}
                <input type="file" accept=".pdf,image/jpeg,image/jpg" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) { if (f.size > 10485760) { setError('File must be under 10MB'); return; } setCertFile(f); setCertFileName(f.name); } }} />
              </label>
            </div>
          </div>
          <div className="panel-actions">
            <button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : renewFromId ? 'Save Renewal' : 'Save Certificate'}</button>
            <button onClick={() => { setShowAdd(false); setRenewFromId(null); setError(''); setCertFile(null); setCertFileName(''); }} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {!loading && certs.length > 0 && (
        <div className="toolbar">
          <div className="search-box">
            <span style={{ color: '#94a3b8', fontSize: 14 }}>🔍</span>
            <input placeholder="Search certificate or address…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="cv-filter" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
            {CERT_FILTERS.map(f => (
              <option key={f} value={f}>{f}{f !== 'All' && filterCount(f) > 0 ? ` (${filterCount(f)})` : ''}</option>
            ))}
          </select>
          <span className="result-info">{displayed.length} shown</span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 15 }}>Loading certificates…</div>
      ) : certs.length === 0 ? (
        <div className="cv-empty">
          <div className="empty-icon">📋</div>
          <h3>No certificates recorded yet</h3>
          <p>Track Gas Safety, EPCs, EICRs, HMO licences and more.<br />Get alerted before they expire.</p>
          <button className="btn-primary" onClick={() => openAdd()}>+ Add Your First Certificate</button>
        </div>
      ) : displayed.length === 0 ? (
        <div className="cv-empty">
          <div className="empty-icon">🔍</div>
          <h3>No certificates match</h3>
          <p>Try a different filter or clear your search.</p>
          <button className="btn-secondary" onClick={() => { setFilterTab('All'); setSearch(''); }}>Clear filters</button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div className="cv-table-wrap">
            <table className="cv-table">
              <thead>
                <tr>
                  <th>Certificate</th>
                  <th>Property</th>
                  <th>Issued</th>
                  <th>Valid Until</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map(c => {
                  const st = certStatus(c);
                  const isHistory = Boolean(c.superseded_by);
                  const isEditing = editingCertId === c.id;
                  return (
                    <React.Fragment key={c.id}>
                      <tr className="cv-data-row">
                        <td>
                          <div className="cert-type">
                            {c.cert_type}
                            {isHistory && <span className="history-badge">Superseded</span>}
                            {!isHistory && c.shared_with_tenant && c.file_url && <span className="share-badge on">Shared</span>}
                            {!isHistory && isStatutory(c.cert_type) && c.file_url && !c.shared_with_tenant && <span className="share-badge off">Not shared</span>}
                          </div>
                          {c.description && <div className="cert-note">{c.description}</div>}
                        </td>
                        <td><div className="cert-prop">{c.property_address}</div></td>
                        <td><div className="cert-date">{fmt(c.issue_date)}</div></td>
                        <td><div className="cert-date">{fmt(c.expiry_date)}</div></td>
                        <td>
                          <span className="status-badge" style={{ background: st.bg, color: st.color }}>{st.icon} {st.label}</span>
                        </td>
                        <td>
                          <div className="actions-cell">
                            {c.file_url && (
                              <a href={c.file_url} target="_blank" rel="noopener noreferrer" className="btn-act" style={{ background: '#f0fdfa', color: '#0d9488', borderColor: '#0d9488' }}>📄 View</a>
                            )}
                            {!isHistory && c.file_url && (
                              <button className="btn-act" style={{ background: c.shared_with_tenant ? '#eff6ff' : '#fffbeb', color: c.shared_with_tenant ? '#1d4ed8' : '#b45309', borderColor: c.shared_with_tenant ? '#93c5fd' : '#fde68a' }}
                                onClick={() => toggleShare(c.id, c.shared_with_tenant)}>
                                {c.shared_with_tenant ? '↗ Shared' : 'Share'}
                              </button>
                            )}
                            {!isHistory && (st.key === 'expired' || st.key === 'expiring') && (
                              <button className="btn-act" style={{ background: '#fff1f1', color: '#dc2626', borderColor: '#fecaca' }}
                                onClick={() => openAdd({ property_address: c.property_address, cert_type: c.cert_type, supersede_id: c.id })}>🔄 Renew</button>
                            )}
                            {!isHistory && st.key === 'expired' && (
                              <button className="btn-act" style={{ background: '#f0f9ff', color: '#012169', borderColor: '#93c5fd' }}
                                onClick={() => logRenewalJob(c)}>🛠️ Job</button>
                            )}
                            {!isHistory && (
                              <button className="btn-act" style={{ background: '#f0f9ff', color: '#1d4ed8', borderColor: '#93c5fd' }}
                                onClick={() => startEditCert(c)}>✏️ Edit</button>
                            )}
                            <button className="btn-act" style={{ background: '#fff', color: '#dc2626', borderColor: '#fecaca' }}
                              onClick={() => deleteCert(c.id)} disabled={deletingCertId === c.id}>
                              {deletingCertId === c.id ? '…' : '🗑️'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isEditing && (
                        <tr className="cv-edit-row">
                          <td colSpan={6}>
                            <div style={{ fontWeight: 800, fontSize: 13, color: '#0d9488', marginBottom: 14 }}>✏️ Editing — {c.cert_type} · {c.property_address}</div>
                            <div className="cv-grid">
                              <div>
                                <label className="field-label">Certificate Type *</label>
                                <select value={editCertForm.cert_type} onChange={e => setEditCertForm((p: any) => ({ ...p, cert_type: e.target.value }))} className="field-input">
                                  {CERT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="field-label">Issue Date *</label>
                                <input type="date" value={editCertForm.issue_date} onChange={e => setEditCertForm((p: any) => ({ ...p, issue_date: e.target.value }))} className="field-input" />
                              </div>
                              <div>
                                <label className="field-label">Valid Until</label>
                                <input type="date" value={editCertForm.expiry_date} onChange={e => setEditCertForm((p: any) => ({ ...p, expiry_date: e.target.value }))} className="field-input" />
                              </div>
                              <div>
                                <label className="field-label">Notes</label>
                                <input type="text" value={editCertForm.notes} onChange={e => setEditCertForm((p: any) => ({ ...p, notes: e.target.value }))} className="field-input" />
                              </div>
                              <div className="span2">
                                <label className="share-row">
                                  <input type="checkbox" checked={!!editCertForm.shared_with_tenant} onChange={e => setEditCertForm((p: any) => ({ ...p, shared_with_tenant: e.target.checked }))} />
                                  Share with tenant in portal
                                </label>
                              </div>
                            </div>
                            <div style={{ marginTop: 12 }}>
                              <label className="field-label">Replace Document (optional)</label>
                              <label className="file-label" style={{ fontSize: 12 }}>
                                📁 {replaceCertFile ? replaceCertFile.name : 'Choose new file…'}
                                <input type="file" accept=".pdf,image/jpeg,image/jpg" style={{ display: 'none' }}
                                  onChange={e => { const f = e.target.files?.[0]; if (f) setReplaceCertFile(f); }} />
                              </label>
                            </div>
                            <div className="panel-actions">
                              <button onClick={() => saveEditCert(c.id)} disabled={saving} className="btn-primary">{saving ? 'Saving…' : '✓ Save Changes'}</button>
                              <button onClick={() => { setEditingCertId(null); setReplaceCertFile(null); }} className="btn-secondary">Cancel</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
